// Data hook for the public, read-only athlete / coach profile page. Unlike the
// live tournament view a profile does not change second-to-second, so it loads
// once (no polling). Resolves to `notfound` when the token is unknown or the
// profile is not public, so a stale or private link shows a friendly message
// rather than an error.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PublicAthleteProfile } from '../../domain';
import { fetchPublicProfile } from '../../data/publicClient';

export type PublicProfileStatus = 'loading' | 'ready' | 'notfound' | 'error';

export interface PublicProfileResult {
  profile: PublicAthleteProfile | null;
  status: PublicProfileStatus;
  error: string | null;
  reload: () => void;
}

export function usePublicProfile(token: string): PublicProfileResult {
  const [profile, setProfile] = useState<PublicAthleteProfile | null>(null);
  const [status, setStatus] = useState<PublicProfileStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const next = await fetchPublicProfile(token);
      if (!mounted.current) return;
      if (next === null) {
        setProfile(null);
        setStatus('notfound');
      } else {
        setProfile(next);
        setStatus('ready');
        setError(null);
      }
    } catch (e: unknown) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load profile.');
      setStatus('error');
    }
  }, [token]);

  useEffect(() => {
    mounted.current = true;
    void load();
    return () => {
      mounted.current = false;
    };
  }, [load]);

  return { profile, status, error, reload: () => void load() };
}
